import { useId, useRef } from 'react';
import usePrefersReducedMotion from '../hooks/usePrefersReducedMotion';
import usePortalTrace from '../hooks/usePortalTrace';
import portalArt from '../assets/img/portal-o.webp';
import {
  CAP, BOX_H, WORD_W, LETTERS, X, PRIMARY, PARTY_COLORS, TRACE, PORTAL_ART_SCALE, GRADIENT_SPAN,
} from '../utils/portalWordmark';

const STROKE = { fill: 'none', strokeWidth: 28, strokeLinecap: 'butt', strokeLinejoin: 'miter' };
// P, (portal = slot 1), R, T, A, L — reading order drives the stagger.
export const WORD = [['P', 0], ['R', 2], ['T', 3], ['A', 4], ['L', 5]];

/** The seven party colours, fixed in place. Static on purpose: a scrolling gradient repaints every frame. */
export function PartyGradient({ id, x2 = GRADIENT_SPAN, units = 'userSpaceOnUse' }) {
  const stops = [...PARTY_COLORS, PARTY_COLORS[0]];
  return (
    <linearGradient id={id} gradientUnits={units} x1="0" y1="0" x2={x2} y2={units === 'userSpaceOnUse' ? 0 : 1} spreadMethod="repeat">
      {stops.map((c, i) => <stop key={i} offset={i / PARTY_COLORS.length} stopColor={c} />)}
    </linearGradient>
  );
}

/**
 * The Portal logo: the spinning portal IS the O, inside the drawn word. A streak of
 * party colour traces the letters in reading order and the portal swells as the
 * streak passes it (hooks/usePortalTrace). The ink letters never leave — the streak
 * paints over them — so every frame reads as the logo. This is the logo everywhere
 * the word fits; where it doesn't, PortalMark.
 *
 * Minimum cap 24. Colour is inherited: put text-black / text-white on an ancestor,
 * nothing else, ever. The portal is never recoloured and always spins — except
 * under prefers-reduced-motion (or `still`), when it holds and there is no trace.
 *
 * The art overhangs the box: its tails reach ~0.4 × cap above and below and run
 * behind the P and the R. The box is overflow: visible; give the parent that room
 * or the tails clip.
 */
export default function PortalLogo({ cap = 24, className = '', still, speed = 1 }) {
  const reduced = usePrefersReducedMotion();
  const frozen = still ?? reduced;
  const root = useRef(null);
  const gid = `pl-grad-${useId().replace(/:/g, '')}`;
  usePortalTrace(root, { reduced: frozen, speed });

  const o = PRIMARY.o;
  const inset = `${(-(PORTAL_ART_SCALE - 1) / 2) * 100}%`;

  return (
    <span
      ref={root}
      role="img"
      aria-label="Portal"
      className={className}
      style={{ position: 'relative', display: 'inline-block', width: (cap * WORD_W) / CAP, height: (cap * BOX_H) / CAP }}
    >
      {/* The portal first, so the letters sit on top of its tails. */}
      <span aria-hidden style={{ position: 'absolute', left: `${o.left}%`, top: 0, width: `${o.width}%`, height: `${o.height}%` }}>
        <span data-pulse style={{ position: 'absolute', inset, display: 'block' }}>
          <img
            src={portalArt}
            alt=""
            draggable="false"
            // The share card lifts this out of the still and spins it itself, frame by frame.
            data-portal-o
            className={frozen ? undefined : 'pr-portal-spin'}
            style={{ display: 'block', width: '100%', height: '100%' }}
          />
        </span>
      </span>
      <svg aria-hidden viewBox={`0 0 ${WORD_W} ${BOX_H}`} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible' }}>
        <defs><PartyGradient id={gid} /></defs>
        <g {...STROKE} stroke="currentColor">
          {WORD.map(([k]) => <path key={k} d={LETTERS[k]} transform={`translate(${X[k]},0)`} />)}
        </g>
        {!frozen && (
          <g {...STROKE} stroke={`url(#${gid})`}>
            {WORD.map(([k, slot]) => (
              <path
                key={k}
                d={LETTERS[k]}
                transform={`translate(${X[k]},0)`}
                pathLength="100"
                strokeDasharray={`${TRACE.dashLen} 200`}
                strokeDashoffset={TRACE.dashLen}
                data-trace={slot}
              />
            ))}
          </g>
        )}
      </svg>
    </span>
  );
}
